import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import { Box, Chip, Collapse, Link, Stack, Tooltip, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useState, type ReactNode } from 'react';
import type { ApplicationTabKey } from '../../lib/applicationDetailTabStatus';
import {
  type PdlDecisionEvidence,
  summarizePdlDebtEvidence,
  summarizePdlIncomeEvidence,
} from '../../lib/pdlDecisionEvidence';
import { PDL_EVIDENCE_COPY } from '../../lib/pdlAdjudicatorUiCopy';
import { PDL_CHIP_SX, PDL_INNER_RADIUS, PDL_SECTION_LABEL_SX } from '../../lib/pdlDecisionChrome';
import { buildPdlObligationTable } from '../../lib/pdlExistingLoansRows';
import { PdlExistingLoansTable } from './PdlExistingLoansTable';

function fmt(n: number): string {
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 0,
  }).format(n);
}

function EvidenceTile({
  title,
  hint,
  summary,
  accent,
  icon,
  expanded,
  onToggle,
  children,
  testId,
}: {
  title: string;
  /** Plain-language meaning of the title, shown on an ⓘ next to it. */
  hint?: string;
  summary: string;
  accent: 'info' | 'warning' | 'success';
  icon: ReactNode;
  expanded: boolean;
  onToggle: () => void;
  children: ReactNode;
  testId: string;
}) {
  const theme = useTheme();
  const palette = theme.palette[accent];

  return (
    <Box
      data-testid={testId}
      sx={{
        borderRadius: PDL_INNER_RADIUS,
        border: '1px solid',
        borderColor: alpha(palette.main, 0.28),
        bgcolor: alpha(palette.main, 0.04),
        overflow: 'hidden',
      }}
    >
      <Box
        component="button"
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          width: '100%',
          textAlign: 'left',
          border: 'none',
          cursor: 'pointer',
          p: 1.1,
          pl: 1.25,
          bgcolor: 'transparent',
          borderLeft: '4px solid',
          borderLeftColor: palette.main,
          '&:hover': { bgcolor: alpha(palette.main, 0.06) },
          '&:focus-visible': {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: 2,
          },
        }}
      >
        <Box sx={{ color: palette.dark, pt: 0.15, flexShrink: 0 }} aria-hidden>
          {icon}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 800, color: palette.dark, lineHeight: 1.3, display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            {title}
            {hint ? (
              <Tooltip title={hint} arrow placement="top">
                <InfoOutlinedIcon
                  aria-label={hint}
                  onClick={(e) => e.stopPropagation()}
                  sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }}
                />
              </Tooltip>
            ) : null}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, lineHeight: 1.4 }}>
            {summary}
          </Typography>
        </Box>
        <ExpandMoreRoundedIcon
          sx={{
            color: 'text.secondary',
            transform: expanded ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.2s ease',
            '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
          }}
        />
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ px: 1.25, pb: 1.15, pt: 0.15 }}>{children}</Box>
      </Collapse>
    </Box>
  );
}

export function PdlDecisionEvidenceStrip({
  evidence,
  loading = false,
  embedded = false,
  onNavigate,
}: {
  evidence: PdlDecisionEvidence | null;
  loading?: boolean;
  embedded?: boolean;
  onNavigate?: (key: ApplicationTabKey) => void;
}) {
  const [incomeOpen, setIncomeOpen] = useState(false);
  const [debtOpen, setDebtOpen] = useState(false);

  if (!loading && !evidence?.income && !evidence?.debt) return null;

  const income = evidence?.income ?? null;
  const debt = evidence?.debt ?? null;
  const hasObligationTable = buildPdlObligationTable(debt?.pdlExistingLoans) != null;

  return (
    <Stack
      spacing={1}
      data-testid="pdl-decision-evidence-strip"
      data-tour-target="pdl-decision-evidence"
      sx={{
        px: embedded ? 1.25 : 0,
        pb: embedded ? 1.15 : 0,
        pt: embedded ? 0 : 0,
        borderTop: embedded ? '1px solid' : 'none',
        borderColor: embedded ? 'divider' : undefined,
      }}
    >
      <Typography variant="caption" sx={PDL_SECTION_LABEL_SX}>
        {PDL_EVIDENCE_COPY.sectionTitle}
      </Typography>

      {loading ? (
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.45, pl: 0.25 }}>
          Loading income streams from verification…
        </Typography>
      ) : null}

      {!loading && income ? (
        <EvidenceTile
          testId="pdl-evidence-income"
          title={PDL_EVIDENCE_COPY.incomeTitle}
          hint={PDL_EVIDENCE_COPY.incomeHint}
          summary={summarizePdlIncomeEvidence(income)}
          accent="info"
          icon={<PaymentsOutlinedIcon fontSize="small" />}
          expanded={incomeOpen}
          onToggle={() => setIncomeOpen((v) => !v)}
        >
          {income.streams.length > 0 ? (
            <Stack spacing={0.65}>
              {income.streams.map((stream) => (
                <Box
                  key={stream.payor}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr auto' },
                    gap: 0.5,
                    alignItems: 'center',
                    py: 0.45,
                    borderBottom: '1px dashed',
                    borderColor: 'divider',
                    '&:last-of-type': { borderBottom: 'none' },
                  }}
                >
                  <Box>
                    <Stack direction="row" spacing={0.5} alignItems="center" useFlexGap flexWrap="wrap">
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {stream.payor}
                      </Typography>
                      {stream.isPrimary ? (
                        <Chip size="small" label="Primary" color="info" sx={{ ...PDL_CHIP_SX, fontWeight: 800 }} />
                      ) : null}
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      {[stream.payorType, stream.frequency].filter(Boolean).join(' · ') || 'Bank deposit stream'}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 800, fontVariantNumeric: 'tabular-nums', color: 'info.dark' }}
                  >
                    {fmt(stream.monthlyAmount)}/mo
                  </Typography>
                </Box>
              ))}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.45 }}>
              {income.primaryPayor
                ? `Primary payor: ${income.primaryPayor}`
                : 'Stream breakdown not in snapshot — open Income tab for deposits.'}
            </Typography>
          )}
          {income.deMonthlyIncome != null && income.totalMonthly != null ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
              {income.incomeAligned === false
                ? `Decision Engine used ${fmt(income.deMonthlyIncome)}/mo (differs from income service total).`
                : `Decision Engine matches income service total ${fmt(income.totalMonthly)}/mo.`}
            </Typography>
          ) : null}
          {onNavigate ? (
            <Link
              component="button"
              type="button"
              underline="hover"
              onClick={() => onNavigate('income')}
              sx={{ fontWeight: 700, fontSize: '0.8125rem', mt: 0.75, display: 'inline-block' }}
            >
              {PDL_EVIDENCE_COPY.openIncomeTab}
            </Link>
          ) : null}
        </EvidenceTile>
      ) : null}

      {!loading && debt ? (
        <EvidenceTile
          testId="pdl-evidence-debt"
          title={PDL_EVIDENCE_COPY.debtTitle}
          hint={PDL_EVIDENCE_COPY.debtHint}
          summary={summarizePdlDebtEvidence(debt)}
          accent={debt.riskFlags.length > 0 ? 'warning' : 'success'}
          icon={<AccountBalanceOutlinedIcon fontSize="small" />}
          expanded={debtOpen}
          onToggle={() => setDebtOpen((v) => !v)}
        >
          <Stack spacing={0.5}>
            {hasObligationTable ? (
              <PdlExistingLoansTable
                pdlExistingLoans={debt.pdlExistingLoans}
                accent={debt.riskFlags.length > 0 ? 'warning' : 'success'}
              />
            ) : (
              // Applications evaluated before obligations were stamped: summary only.
              <>
                <Typography variant="body2">
                  <strong>{PDL_EVIDENCE_COPY.existingPayments}:</strong>{' '}
                  {fmt(debt.monthlyDebtService)}/mo
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>{PDL_EVIDENCE_COPY.elodStatus}:</strong>{' '}
                  {debt.elodAvailable === false ? 'Not available' : 'Connected'}
                </Typography>
              </>
            )}
            {debt.riskFlags.length > 0 ? (
              <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ pt: 0.35 }}>
                {debt.riskFlags.map((flag) => (
                  <Chip key={flag} size="small" color="warning" variant="outlined" label={flag} sx={PDL_CHIP_SX} />
                ))}
              </Stack>
            ) : null}
          </Stack>
        </EvidenceTile>
      ) : null}
    </Stack>
  );
}
